import { ITableColumnSizingState, TSelectableTableRow } from '@shared/types';
import { TItemType, TResolvedItemsMap } from '@modules/items';
import { Dispatch, SetStateAction } from 'react';
import {
  TableCell,
  TableCellLayout,
  TableRow,
  Link as FluentLink,
  tokens,
  Dropdown,
  Option,
  Button,
  Tooltip,
  TableCellActions,
  makeStyles,
} from '@fluentui/react-components';
import { ControlledTooltip } from '@shared/components';
import { getItemPathByItemType } from '@modules/documents';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { OpenRegular, WarningFilled } from '@fluentui/react-icons';
import { sanitizeSnippetWithEm } from '@shared/helpers';
import { buildHref } from '@shared/utils';
import { isIDetectedSelfReference, IDetectedReference, IDetectedSelfReference } from '../../../types/references.types';
import { IDetectedInlineBlock } from '../../../types/inlineBlocks.types';
import { IItemDefinition, TDetectionsRowItem, TDetectionsRowVariant } from '../../../types/shared.types';

type TProps<TChosenTargetItemType extends TItemType> = TSelectableTableRow<TDetectionsRowItem> & {
  variant?: TDetectionsRowVariant; // default 'references'
  sourceId: string; // active group id (for refs: sourceDocument; for IB: document id)
  resolvedItems: TResolvedItemsMap;
  columnSizing: ITableColumnSizingState;

  // references-only (optional)
  chosenTargetItemMap?: Map<string, IItemDefinition<TChosenTargetItemType>>;
  setChosenTargetItemMap?: Dispatch<SetStateAction<Map<string, IItemDefinition<TChosenTargetItemType>>>>;
};

const useStyles = makeStyles({
  description: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '& em': {
      color: tokens.colorBrandForeground1,
      fontWeight: 600,
    },
  },
  detectedCellLayout: {
    paddingTop: '3px',
    paddingBottom: '3px',
    paddingRight: '42px',
  },
});

const DetectionsRow = <TChosenTargetItemType extends TItemType>({
  variant = 'references',
  sourceId,
  resolvedItems,
  item: detectedElem,
  selectionCell,
  preventSelect,
  columnSizing,
  chosenTargetItemMap,
  setChosenTargetItemMap,
  ...tableRowAttributes
}: TProps<TChosenTargetItemType>) => {
  const classes = useStyles();

  // Resolve the source document by group id (works for both variants)
  const srcDoc = resolvedItems[sourceId]?.item;
  const srcHeadings = resolvedItems[sourceId]?.resolvedHeadings;

  if (!srcDoc) return null;

  // --- Common left cell (detected content) ----------------------
  const contextHtml = detectedElem?.contextSnippet ?? '';

  const snippetText = detectedElem?.snippet ?? '';

  // If present
  const warnings: string[] = detectedElem?.warnings ?? [];

  // --- Variant: references --------------------------------------
  const isSelfRef =
    variant === 'references' && isIDetectedSelfReference(detectedElem as IDetectedReference | IDetectedSelfReference);
  const isExternalRef = variant === 'references' && !isSelfRef && !!(detectedElem as IDetectedReference).targetItems;

  // --- Variant: inline-blocks -----------------------------------
  const isInlineBlockRow = variant === 'inline-blocks' && !!(detectedElem as IDetectedInlineBlock).targetInlineBlocks;

  // For references (external): compute target items + selection
  let chosenTargetItemId: string | undefined;
  let chosenTargetItem: any | undefined;
  let chosenTargetLabel = '';
  let targetItems: any[] = [];

  if (isExternalRef) {
    const ref = detectedElem as IDetectedReference;
    targetItems = ref.targetItems.map((targetItem) => resolvedItems[targetItem.item]?.item).filter(Boolean);
    chosenTargetItemId = chosenTargetItemMap?.get(ref.id)?.item;
    chosenTargetItem = chosenTargetItemId && resolvedItems[chosenTargetItemId]?.item;
    chosenTargetLabel = chosenTargetItem ? getReferenceItemLabel(chosenTargetItem.name) : '';

    if (targetItems.some((doc) => !doc) || !chosenTargetItem) {
      // Keep references’ behavior: show nothing if resolution fails (and log)
      console.error(
        `Resolved item not found for source: ${srcDoc._id}, targets: ${ref.targetItems.join(', ')}, selected: ${chosenTargetItemId}!`
      );
      return null;
    }
  }

  // For self refs: compute heading text (same as before)
  const heading =
    isSelfRef &&
    (detectedElem as IDetectedSelfReference).headingId &&
    srcHeadings?.[(detectedElem as IDetectedSelfReference).headingId]
      ? `${srcHeadings[(detectedElem as IDetectedSelfReference).headingId].numbering} ${
          srcHeadings[(detectedElem as IDetectedSelfReference).headingId].text
        }`
      : 'Unknown heading';

  // For inline-blocks: resolve block name
  const inlineBlockItem = isInlineBlockRow
    ? resolvedItems[(detectedElem as IDetectedInlineBlock).targetInlineBlocks[0]]?.item // TODO: change
    : undefined;

  return (
    <TableRow {...tableRowAttributes} className="!hover:radius-0">
      {selectionCell}

      {/* Detected content */}
      <TableCell {...columnSizing?.getTableCellProps('detected')}>
        <TableCellLayout
          truncate
          className={classes.detectedCellLayout}
          description={
            <span
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: sanitizeSnippetWithEm(contextHtml) }}
            />
          }
        >
          <ControlledTooltip enable={srcDoc.name.length > 38} content={srcDoc.name} relationship="description">
            <FluentLink
              href={buildHref(`/${getItemPathByItemType(srcDoc.type)}/${srcDoc._id}`, {
                elem: (detectedElem as IDetectedReference)?.elementId,
                highlightText: snippetText,
              })}
              target="_blank"
              onClick={preventSelect}
              appearance="subtle"
            >
              {snippetText}
            </FluentLink>
          </ControlledTooltip>
        </TableCellLayout>

        {warnings.length > 0 && (
          <TableCellActions visible className="mr-5">
            <Tooltip
              content={
                warnings.length === 1 ? (
                  warnings[0]
                ) : (
                  <ul className="list-disc pl-2 space-y-1">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )
              }
              relationship="label"
            >
              <WarningFilled fontSize={20} color="#c43501" />
            </Tooltip>
          </TableCellActions>
        )}
      </TableCell>

      {/* Arrow */}
      <TableCell {...columnSizing?.getTableCellProps('arrow')}>
        <svg
          width="100%"
          height="12"
          viewBox="0 0 100 12"
          preserveAspectRatio="xMidYMid meet"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line x1="0" y1="6" x2="96" y2="6" stroke={tokens.colorBrandBackground} strokeWidth="1.5" />
          <polygon points="96,2 100,6 96,10" fill={tokens.colorBrandBackground} />
        </svg>
      </TableCell>

      {/* Right column */}
      <TableCell {...columnSizing?.getTableCellProps('referenced')}>
        {variant === 'references' ? (
          // Self-reference
          isSelfRef ? (
            <TableCellLayout truncate style={{ paddingTop: '3px', paddingBottom: '3px' }}>
              <ControlledTooltip enable={heading.length > 38} content={heading} relationship="description">
                <FluentLink
                  href={`/${getItemPathByItemType(srcDoc.type)}/${srcDoc._id}?elem=${
                    (detectedElem as IDetectedSelfReference).headingId
                  }`}
                  target="_blank"
                  onClick={preventSelect}
                >
                  {heading}
                </FluentLink>
              </ControlledTooltip>
            </TableCellLayout>
          ) : // External reference
          chosenTargetItem ? (
            <>
              {targetItems.length === 1 ? (
                <TableCellLayout truncate style={{ paddingTop: '3px', paddingBottom: '3px' }}>
                  <ControlledTooltip
                    enable={chosenTargetLabel.length > 38}
                    content={chosenTargetLabel}
                    relationship="description"
                  >
                    <FluentLink
                      href={`/${getItemPathByItemType(chosenTargetItem.type)}/${chosenTargetItem._id}`}
                      target="_blank"
                      onClick={preventSelect}
                    >
                      {chosenTargetLabel}
                    </FluentLink>
                  </ControlledTooltip>
                </TableCellLayout>
              ) : (
                <div className="flex items-center space-x-2">
                  <Dropdown
                    selectedOptions={[chosenTargetItem._id]}
                    onOptionSelect={(e, data) => {
                      e.stopPropagation();
                      const newChosenTargetItemId = data.selectedOptions[0];
                      const targetItem = resolvedItems[newChosenTargetItemId]?.item;
                      if (targetItem && chosenTargetItemMap && setChosenTargetItemMap) {
                        setChosenTargetItemMap((prev) => {
                          const m = new Map(prev);
                          m.set((detectedElem as IDetectedReference).id, {
                            item: targetItem._id,
                            itemType: targetItem.type as TChosenTargetItemType,
                          });
                          return m;
                        });
                      } else {
                        console.error(`Resolved item not found for target document: ${newChosenTargetItemId}`);
                      }
                    }}
                    onClick={preventSelect}
                    value={chosenTargetLabel}
                    className="!w-[275px] truncate"
                  >
                    {targetItems.map((doc) => (
                      <Option key={doc._id} value={doc._id}>
                        {getReferenceItemLabel(doc.name)}
                      </Option>
                    ))}
                  </Dropdown>
                  <Button
                    as="a"
                    href={`/${getItemPathByItemType(chosenTargetItem.type)}/${chosenTargetItem._id}`}
                    target="_blank"
                    onClick={preventSelect}
                    size="small"
                    appearance="subtle"
                    icon={<OpenRegular />}
                  />
                </div>
              )}
            </>
          ) : null
        ) : inlineBlockItem ? (
          <TableCellLayout truncate style={{ paddingTop: '3px', paddingBottom: '3px' }}>
            <ControlledTooltip
              enable={inlineBlockItem.name.length > 38}
              content={inlineBlockItem.name}
              relationship="description"
            >
              <FluentLink
                href={`/${getItemPathByItemType(inlineBlockItem.type)}/${inlineBlockItem._id}`}
                target="_blank"
                onClick={preventSelect}
              >
                {inlineBlockItem.name}
              </FluentLink>
            </ControlledTooltip>
          </TableCellLayout>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default DetectionsRow;
